import { PsHttpService } from '@patsnap/tahiti-core/common/ps-http';
import type { PsHttp, PatSnapSuccessResponseData } from '@patsnap/tahiti-core/common/ps-http';
import type { PsHttpChannel } from '@patsnap/tahiti-core';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { assistantApiUrlPrefix } from '@telecom/shared/domain-config';

/**
 * 基于 Redis 中已保存参数启动 assistant 会话的请求。
 */
export interface ExternalRedisStartRequest {
    /** Redis 中保存任务参数的 key */
    redis_key: string;
    /** 会话标题 */
    title?: string;
    /** 指定启动的 Skill ID */
    skill_id?: string;
    /** 是否使用 flow 模式 */
    use_flow?: boolean;
    /** 首轮任务语言 */
    lang?: string;
    /** Redis payload 中作为首轮 prompt 的字段 */
    text_field?: string;
    /** 是否让 f-chat-agent 后端追加通用启动模板 */
    wrap_prompt?: boolean;
}

/**
 * 基于 Redis 参数启动 assistant 会话后的响应。
 */
export interface ExternalRedisStartResponse {
    /** 新建或启动的 assistant 会话 ID */
    conversation_id: string;
    /** 后端返回的会话对象 */
    conversation?: unknown;
    /** 会话 WebSocket 地址 */
    ws_url?: string;
    /** 读取的 Redis key */
    redis_key?: string;
    /** 读取文本长度 */
    text_length?: number;
    /** 是否已登记待启动消息 */
    pending_start?: boolean;
}

/**
 * 保存任务参数到 Redis 的请求。
 */
export interface ExternalRedisSaveRequest<TPayload = unknown> {
    /** 任意 JSON 任务参数 payload */
    payload: TPayload;
    /** 可选过期时间（秒），不传由后端默认值决定 */
    ttl_seconds?: number;
}

/**
 * 保存任务参数到 Redis 的响应。
 */
export interface ExternalRedisSaveResponse {
    /** 后端生成的 Redis key */
    redis_key: string;
    /** 过期时间（秒） */
    expires_in: number;
}

/**
 * 查询 Redis 中已保存任务参数的请求。
 */
export interface ExternalRedisParamsRequest {
    /** Redis key */
    redis_key: string;
    /** 可选字段路径 */
    field?: string;
}

/**
 * 查询 Redis 中已保存任务参数的响应。
 */
export interface ExternalRedisParamsResponse<TPayload = unknown> {
    /** Redis key */
    redis_key: string;
    /** Redis 中保存的 payload 或指定字段值 */
    payload: TPayload;
}

/**
 * f-chat-agent 外部启动相关接口。
 */
export interface AssistantChatApi {
    /**
     * 使用 Redis 中保存的任务参数创建 assistant 会话。
     */
    createExternalRedisConversation(data: ExternalRedisStartRequest): Promise<ExternalRedisStartResponse>;

    /**
     * 保存任务参数到 Redis，返回后端生成的 key。
     */
    saveExternalRedisParams<TPayload = unknown>(data: ExternalRedisSaveRequest<TPayload>): Promise<ExternalRedisSaveResponse>;

    /**
     * 查询 Redis 中保存的任务参数。
     */
    getExternalRedisParams<TPayload = unknown>(params: ExternalRedisParamsRequest): Promise<ExternalRedisParamsResponse<TPayload>>;
}

/**
 * AssistantChatApi 的 IOC 注入标识符。
 */
export const AssistantChatApi = Symbol('AssistantChatApi');

const useLocalAssistantApi = false;
const localAssistantApiUrlPrefix = 'http://127.0.0.1:8010/api';

/**
 * f-chat-agent 外部启动相关接口实现。
 * @description skill_chat_backend 返回裸 JSON（无 PatSnap 信封），
 * 但仍通过 PsHttpService 发起请求以复用鉴权、错误处理等拦截器链，
 * 故响应需强制转型为后端的实际结构。
 */
@injectable()
export class AssistantChatApiImpl extends PsHttpService implements AssistantChatApi {
    constructor(httpClient: PsHttp, httpChannelClient: PsHttpChannel) {
        super(httpClient, httpChannelClient);
    }

    get options() {
        return {
            baseURL: useLocalAssistantApi ? localAssistantApiUrlPrefix : assistantApiUrlPrefix,
            headers: {
                'Content-Type': 'application/json',
            },
        };
    }

    async createExternalRedisConversation(data: ExternalRedisStartRequest): Promise<ExternalRedisStartResponse> {
        const result = await this.post<PatSnapSuccessResponseData<ExternalRedisStartResponse>>({
            url: '/chat/external/redis-start',
            data,
        });
        return result as unknown as ExternalRedisStartResponse;
    }

    async getExternalRedisParams<TPayload = unknown>(params: ExternalRedisParamsRequest): Promise<ExternalRedisParamsResponse<TPayload>> {
        const result = await this.get<PatSnapSuccessResponseData<ExternalRedisParamsResponse<TPayload>>>({
            url: '/chat/external/redis-params',
            params,
        });
        return result as unknown as ExternalRedisParamsResponse<TPayload>;
    }

    async saveExternalRedisParams<TPayload = unknown>(data: ExternalRedisSaveRequest<TPayload>): Promise<ExternalRedisSaveResponse> {
        const result = await this.post<PatSnapSuccessResponseData<ExternalRedisSaveResponse>>({
            url: '/chat/external/redis-save',
            data,
        });
        return result as unknown as ExternalRedisSaveResponse;
    }
}
